import { Box, Text, VStack, Spinner } from "@chakra-ui/react";
import { Pedido as IPedido, StatusPedido, Emissor } from "../../utils/interfaces";
import { formatBRL } from "../../utils/currency";
import { Input } from "../../components/Form/Input";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { ordemTrabalhoSchema, OrdemTrabalhoFormData } from "../../schemas/ordemTrabalho";
import { useEffect } from "react";

interface Props {
  pedido: IPedido
  emissor: Emissor
  onOrdemTrabalhoChange?: (ordemTrabalho: string) => void;
  isUpdating?: boolean;
}

const color: Record<StatusPedido, string> = {
  CANCELADO: 'red.500',
  ABERTO: 'blue.500',
  FINALIZADO: 'green.500',
  RESERVADO: 'purple.500',
  "ESTORNO PENDENTE": 'purple.500',
  PROCESSANDO: 'orange.500',
  DEVOLVIDO: 'red.500',
  "DEVOLVIDO PARCIALMENTE": 'red.500',
  CONTESTADO: 'orange.500',
};

export function Pedido({pedido, emissor, onOrdemTrabalhoChange, isUpdating = false}: Props){
  const {
    register,
    formState: { errors },
    watch,
    setValue,
    reset
  } = useForm<OrdemTrabalhoFormData>({
    resolver: zodResolver(ordemTrabalhoSchema),
    defaultValues: {
      ordem_trabalho: pedido.ordem_trabalho || ''
    }
  });

  // Verifica se a agência tem a configuração habilitada
  const showOrdemTrabalho = emissor?.show_ordem_trabalho;

  // Garante que o valor seja atualizado quando o pedido mudar
  useEffect(() => {
    reset({
      ordem_trabalho: pedido.ordem_trabalho || ''
    });
  }, [pedido.ordem_trabalho, reset]);

  const handleOrdemTrabalhoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setValue('ordem_trabalho', value);
  };

  const handleOrdemTrabalhoBlur = () => {
    const value = watch('ordem_trabalho');
    // Só salva se o valor mudou e não está vazio
    if (value !== pedido.ordem_trabalho && value.trim() !== '') {
      onOrdemTrabalhoChange?.(value);
    }
  };

  const handleOrdemTrabalhoKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      const value = watch('ordem_trabalho');
      // Só salva se o valor mudou e não está vazio
      if (value !== pedido.ordem_trabalho && value.trim() !== '') {
        onOrdemTrabalhoChange?.(value);
      }
      e.currentTarget.blur(); // Remove o foco do campo
    }
  };

  return (
    <Box w={'500px'} bg={'#f4f4f4'} p={'2'} display={'flex'} flexDir={'column'}>
      <Text fontWeight={'bold'}>Detalhes do Pedido</Text>
      <Text fontSize={'small'}>Código do Pedido: {pedido.id}</Text>
      <Text fontSize={'small'} color={color[pedido.status_label]}>Status do Pedido: {pedido.status_label}</Text>
      {pedido.erro && <Text fontSize={'small'} color={color[pedido.status_label]}>{pedido.erro}</Text>}
            
      {showOrdemTrabalho && (
        <VStack spacing={3} mt={3}>
          <Box position="relative" width="100%">
            <Box display="flex" alignItems="center" gap={3}>
              <Text fontSize="sm" fontWeight="medium" minW="120px">
                Ordem de Trabalho *
              </Text>
              <Box flex={1} position="relative">
                <Input
                  {...register('ordem_trabalho')}
                  name="ordem_trabalho"
                  type="text"
                  placeholder="Digite a ordem de trabalho"
                  error={errors.ordem_trabalho}
                  isDisabled={isUpdating}
                  maxLength={20}
                  onChange={handleOrdemTrabalhoChange}
                  onBlur={handleOrdemTrabalhoBlur}
                  onKeyPress={handleOrdemTrabalhoKeyPress}
                  label="" // Remove o label do input
                />
                {isUpdating && (
                  <Box position="absolute" right="3" top="50%" transform="translateY(-50%)">
                    <Spinner size="sm" color="purple.500" />
                  </Box>
                )}
              </Box>
            </Box>
            {errors.ordem_trabalho && (
              <Text fontSize="xs" color="red.500" mt={1} ml="120px">
                {errors.ordem_trabalho.message}
              </Text>
            )}
          </Box>
          <Text fontSize={'xs'} color={'gray.500'} textAlign={'center'}>
            💡 Dica: Pressione Enter ou clique fora do campo para salvar
          </Text>
        </VStack>
      )}
      
      <Text fontWeight={'bold'} fontSize={{sm: 'lg', md: '2xl'}} textTransform={'uppercase'} color={'red'} mt={3}>Total do Pedido: {formatBRL(Number(pedido.valor_total))}</Text>
    </Box>
  )
}
